import React from "react";
import { Button, Skeleton } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarDays, CircleAlert, Clock3, Image as ImageIcon,
  MapPin, Megaphone, RefreshCw, Store, Tag,
} from "lucide-react";
import useHomeScheduledPosts from "./useHomeScheduledPosts";

const formatPostTime = (value) => new Intl.DateTimeFormat("en-GB", {
  weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
}).format(new Date(value));

function postTitle(post) {
  const caption = post.caption?.trim();
  if (caption) return caption;
  return post.original_filename?.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ") || "Scheduled Google post";
}

export default function HomeBusinessProfilePosts() {
  const { posts, isLoading, isError, refetch } = useHomeScheduledPosts();
  const upcoming = posts.slice(0, 2);
  const remainingCount = Math.max(posts.length - upcoming.length, 0);

  return (
    <section className="home-profile-posts" aria-labelledby="home-profile-posts-title" aria-busy={isLoading}>
      <div className="home-profile-posts-copy">
        <div className="home-profile-posts-kicker"><Store size={15} /> GOOGLE BUSINESS PROFILE</div>
        <h2 id="home-profile-posts-title">{upcoming.length ? "Your next posts are ready to meet your guests." : "Give guests something new to discover."}</h2>
        <p>{upcoming.length
          ? `${posts.length} ${posts.length === 1 ? "post is" : "posts are"} scheduled across your locations. Here’s what’s coming next.`
          : "Share a post directly on your Business Profile and keep your latest news visible when guests find you on Google."}</p>

        {!upcoming.length && !isLoading && (
          <div className="home-profile-post-ideas" aria-label="Business Profile post ideas">
            <span><Megaphone size={14} /> What’s new</span>
            <span><Tag size={14} /> An offer</span>
            <span><CalendarDays size={14} /> An event</span>
          </div>
        )}

        {isError && <div className="home-profile-post-error" role="status">
          <CircleAlert size={15} /> Some scheduled posts couldn’t be loaded.
          <button type="button" className="home-retry" onClick={refetch}><RefreshCw size={13} /> Retry</button>
        </div>}

        <div className="home-profile-post-actions">
          <Button asChild size="3"><Link to="/reviews/posts"><ImageIcon size={16} /> Create a post <ArrowRight size={16} /></Link></Button>
        </div>
      </div>

      <div className="home-scheduled-posts" aria-label="Upcoming scheduled posts">
        {isLoading && !upcoming.length ? [0, 1].map((index) => (
          <Skeleton key={index}><div className="home-scheduled-post-loading" /></Skeleton>
        )) : upcoming.length ? upcoming.map((post) => (
          <Link
            className="home-scheduled-post"
            key={`${post.locationId}-${post.id}`}
            to={`/reviews/posts?location=${post.locationId}`}
            aria-label={`View scheduled post for ${post.locationName}`}
          >
            <span className="home-scheduled-post-image">
              {post.image_url ? <img src={post.image_url} alt="" /> : <ImageIcon size={21} />}
            </span>
            <span className="home-scheduled-post-copy">
              <span className="home-scheduled-post-type">{post.destination === "post" ? "GOOGLE POST" : "GOOGLE PHOTO"}</span>
              <strong>{postTitle(post)}</strong>
              <span><Clock3 size={12} /> {formatPostTime(post.scheduled_for)}</span>
              <span><MapPin size={12} /> {post.locationName}</span>
            </span>
            <ArrowRight className="home-scheduled-post-arrow" size={15} />
          </Link>
        )).concat(remainingCount > 0 ? [(
          <Link className="home-scheduled-post-summary" to="/reviews/posts" key="scheduled-post-summary">
            <span className="home-scheduled-post-summary-icon"><CalendarDays size={19} /></span>
            <span><strong>{remainingCount} more {remainingCount === 1 ? "post" : "posts"} scheduled</strong><small>See the full posting calendar</small></span>
            <ArrowRight size={15} />
          </Link>
        )] : []) : (
          <div className="home-profile-post-preview" aria-hidden="true">
            <div className="home-profile-post-preview-image"><ImageIcon size={25} /></div>
            <div className="home-profile-post-preview-body">
              <span className="home-profile-post-preview-label">YOUR BUSINESS</span>
              <strong>Share your next update</strong>
              <span>Turn a moment worth sharing into another reason to visit.</span>
              <div className="home-profile-post-preview-lines"><i /><i /><i /></div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
